import React, { useState, useEffect } from 'react';
import { Save, X, Plus, Trash2, Calendar, Check, AlertCircle, User, Mail, Phone, MapPin, Briefcase, DollarSign, Award, Globe, Shield, FileText, ExternalLink, Edit2 } from 'lucide-react';
import { candidatesAPI } from "../../services/api";
import { toast } from "react-toastify";

// Helper function to format field name
const formatFieldName = (fieldName) => {
  if (!fieldName) return '';
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

// Field type and options detector
const getFieldTypeAndOptions = (fieldName) => {
  const lowerField = fieldName.toLowerCase();
  
  if (lowerField.includes('consent') || lowerField.includes('agree') || 
      lowerField.includes('authorization') || lowerField.includes('permission') ||
      lowerField.includes('verified') || lowerField.includes('approved') ||
      lowerField.includes('confirm')) {
    return { fieldType: 'select', options: ['Yes', 'No'] };
  }
  
  if (lowerField === 'gender') {
    return { fieldType: 'select', options: ['Male', 'Female', 'Other', 'Prefer not to say'] };
  }
  
  if (lowerField.includes('marital')) {
    return { fieldType: 'select', options: ['Single', 'Married', 'Divorced', 'Widowed', 'Prefer not to say'] };
  }
  
  if (lowerField.includes('education') && lowerField.includes('level')) {
    return { fieldType: 'select', options: ['High School', 'Associate Degree', 'Bachelor\'s Degree', 'Master\'s Degree', 'Doctorate', 'Other'] };
  }
  
  if (lowerField.includes('work') && lowerField.includes('authorization')) {
    return { fieldType: 'select', options: ['Citizen', 'Permanent Resident', 'Work Visa', 'Student Visa', 'Other'] };
  }
  
  if (lowerField.includes('visa') && lowerField.includes('status')) {
    return { fieldType: 'select', options: ['H1B', 'L1', 'F1', 'OPT', 'CPT', 'Green Card', 'Citizen', 'Other'] };
  }
  
  if (lowerField.includes('employment') && lowerField.includes('type')) {
    return { fieldType: 'select', options: ['Full-time', 'Part-time', 'Contract', 'Freelance', 'Internship'] };
  }
  
  if (lowerField.includes('salary') && lowerField.includes('frequency')) {
    return { fieldType: 'select', options: ['Annual', 'Monthly', 'Hourly', 'Daily'] };
  }
  
  if (lowerField.includes('notice') && lowerField.includes('period')) {
    return { fieldType: 'select', options: ['Immediate', '15 Days', '30 Days', '45 Days', '60 Days', '90 Days', 'Negotiable'] };
  }
  
  if (lowerField.includes('relocate') || lowerField.includes('relocation')) {
    return { fieldType: 'select', options: ['Yes', 'No', 'Maybe'] };
  }
  
  if (lowerField.includes('currency')) {
    return { fieldType: 'select', options: ['USD', 'INR', 'EUR', 'GBP', 'CAD', 'AUD', 'SGD', 'Other'] };
  }
  
  if (lowerField === 'country' || lowerField.includes('nationality')) {
    return { fieldType: 'select', options: ['India', 'United States', 'United Kingdom', 'Canada', 'Australia', 'Singapore', 'UAE', 'Other'] };
  }
  
  if (lowerField === 'state' && !lowerField.includes('country')) {
    return { fieldType: 'select', options: ['Andhra Pradesh', 'Telangana', 'Karnataka', 'Tamil Nadu', 'Kerala', 'Maharashtra', 'Gujarat', 'Rajasthan', 'Delhi', 'Other'] };
  }
  
  if (lowerField.includes('source')) {
    return { fieldType: 'select', options: ['Direct Application', 'Referral', 'LinkedIn', 'Job Portal', 'Campus', 'Agency', 'Other'] };
  }
  
  if (lowerField.includes('email')) {
    return { fieldType: 'email', options: [] };
  }
  
  if (lowerField.includes('phone') || lowerField.includes('mobile') || lowerField.includes('whatsapp')) {
    return { fieldType: 'tel', options: [] };
  }
  
  if (lowerField.includes('years') || lowerField.includes('experience') || 
      lowerField.includes('ctc') || lowerField.includes('salary') || 
      lowerField.includes('age') || lowerField.includes('gpa') || lowerField.includes('score')) {
    return { fieldType: 'number', options: [] };
  }
  
  if (lowerField.includes('date') || lowerField === 'dob' || lowerField === 'dateofbirth' || lowerField === 'actualjoiningdate') {
    return { fieldType: 'date', options: [] };
  }
  
  if (lowerField.includes('url') || lowerField.includes('website') || 
      lowerField.includes('linkedin') || lowerField.includes('github') || 
      lowerField.includes('portfolio')) {
    return { fieldType: 'url', options: [] };
  }
  
  if (lowerField.includes('address') || lowerField.includes('description') || 
      lowerField.includes('summary') || lowerField.includes('notes') ||
      lowerField.includes('comments') || lowerField.includes('about') ||
      lowerField.includes('explanation') || lowerField.includes('additional')) {
    return { fieldType: 'textarea', options: [] };
  }
  
  return { fieldType: 'text', options: [] };
};

// Get category from field name
const getCategoryFromFieldName = (fieldName) => {
  const lowerField = fieldName.toLowerCase();
  
  if (lowerField.includes('name') || lowerField.includes('dob') || 
      lowerField.includes('date') && lowerField.includes('birth') || 
      lowerField === 'gender' || lowerField === 'age' || lowerField.includes('marital')) {
    return 'Personal';
  }
  
  if (lowerField.includes('email') || lowerField.includes('phone') || 
      lowerField.includes('mobile') || lowerField.includes('whatsapp') ||
      lowerField.includes('linkedin') || lowerField.includes('github') ||
      lowerField.includes('video') || lowerField.includes('portfolio')) {
    return 'Contact';
  }
  
  if (lowerField.includes('address') || lowerField === 'city' || 
      lowerField === 'state' || lowerField === 'country' || 
      lowerField === 'province' || lowerField === 'region' ||
      lowerField.includes('postal') || lowerField.includes('zip')) {
    return 'Address';
  }
  
  if (lowerField.includes('currentjob') || lowerField.includes('currentcompany') || 
      lowerField.includes('previousjob') || lowerField.includes('previousexp') ||
      lowerField.includes('experience') && !lowerField.includes('years') ||
      lowerField.includes('title') || lowerField.includes('employment') || 
      lowerField.includes('notice') || lowerField.includes('career') ||
      lowerField.includes('reason') || lowerField.includes('team')) {
    return 'Employment';
  }
  
  if (lowerField.includes('ctc') || lowerField.includes('salary') || 
      lowerField.includes('compensation') || lowerField.includes('currency') ||
      lowerField.includes('frequency') || lowerField.includes('given') ||
      lowerField.includes('joining') && lowerField.includes('date')) {
    return 'Compensation';
  }
  
  if (lowerField.includes('education') || lowerField.includes('university') || 
      lowerField.includes('degree') || lowerField.includes('gpa') ||
      lowerField.includes('field') && lowerField.includes('study')) {
    return 'Education';
  }
  
  if (lowerField.includes('visa') || lowerField.includes('work') && lowerField.includes('auth') ||
      lowerField.includes('citizen')) {
    return 'Work Authorization';
  }
  
  if (lowerField.includes('reference')) {
    return 'References';
  }
  
  if (lowerField.includes('consent') || lowerField.includes('agree') || 
      lowerField.includes('confirm') || lowerField.includes('permission') ||
      lowerField.includes('verification')) {
    return 'Consents';
  }
  
  if (lowerField.includes('relocate') || lowerField.includes('relocation') ||
      lowerField === 'currentlocation') {
    return 'Location';
  }
  
  if (lowerField.includes('source') || lowerField.includes('referral')) {
    return 'Other';
  }
  
  return 'Other';
};

// Get placeholder text
const getPlaceholderText = (fieldName) => {
  const lowerField = fieldName.toLowerCase();
  
  if (lowerField.includes('email')) return 'example@email.com';
  if (lowerField.includes('phone') || lowerField.includes('mobile')) return '+91 XXXXX XXXXX';
  if (lowerField.includes('linkedin')) return 'https://linkedin.com/in/username';
  if (lowerField.includes('github')) return 'https://github.com/username';
  if (lowerField.includes('portfolio') || lowerField.includes('website')) return 'https://yourwebsite.com';
  if (lowerField.includes('city')) return 'Enter city name';
  if (lowerField.includes('experience')) return 'Years of experience';
  if (lowerField.includes('ctc') || lowerField.includes('salary')) return 'Enter amount';
  
  return `Enter ${formatFieldName(fieldName).toLowerCase()}...`;
};

// Get icon for category
const getCategoryIcon = (category) => {
  const iconMap = {
    'Personal': <User size={20} className="text-blue-600" />,
    'Contact': <Mail size={20} className="text-green-600" />,
    'Address': <MapPin size={20} className="text-red-600" />,
    'Employment': <Briefcase size={20} className="text-indigo-600" />,
    'Compensation': <DollarSign size={20} className="text-green-600" />,
    'Education': <Award size={20} className="text-teal-600" />,
    'Work Authorization': <Shield size={20} className="text-orange-600" />,
    'References': <User size={20} className="text-cyan-600" />,
    'Consents': <Shield size={20} className="text-purple-600" />,
    'Skills': <Award size={20} className="text-yellow-600" />,
    'Location': <Globe size={20} className="text-orange-600" />,
    'Documents': <FileText size={20} className="text-purple-600" />,
  };
  
  return iconMap[category] || <FileText size={20} className="text-gray-600" />;
};

// ✅ Boolean field names that need special handling
const BOOLEAN_FIELD_NAMES = [
  'backgroundCheckConsent',
  'drugTestConsent',
  'rightToWorkConfirm',
  'dataProcessingConsent',
  'communicationsConsent',
  'termsAndConditionsConsent',
  'creditCheckConsent',
  'employmentVerificationConsent',
  'educationVerificationConsent',
  'willingToRelocate',
];

const EditCandidateModal = ({ candidate: initialCandidate, onClose, onSave }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fieldConfigs, setFieldConfigs] = useState([]);
  const [formDataState, setFormDataState] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [candidateData, setCandidateData] = useState(initialCandidate);

  // ✅ UPDATED: Fields that should NOT be rendered (read-only or system fields)
  const EXCLUDED_FIELDS = [
    'technicalSkills', 'softSkills', 'languages', 'preferredLocations', 
    'keyAchievements', 'awardsRecognition', 
    'resumeUrl', 'coverLetterUrl', 'profilePhotoUrl', 
    'certificationsUpload', 'supportingDocuments',
    'professionalLicenses', 'areaProfessionalCertifications',
    'createdAt', 'updatedAt', 'createdBy', 'updatedBy', 
    'id', 'organizationId', 'jobIds', 'applicationId', 
    'clientIds', 'status', 'screeningQuestionsByJob'
  ];

  // Dynamic lists
  const [technicalSkills, setTechnicalSkills] = useState([{ skillName: '', rating: 3 }]);
  const [softSkills, setSoftSkills] = useState([{ skillName: '', rating: 3 }]);
  const [languages, setLanguages] = useState([{ languageName: '', canRead: false, canWrite: false, canSpeak: false }]);
  const [preferredLocations, setPreferredLocations] = useState(['']);
  const [keyAchievements, setKeyAchievements] = useState(['']);
  const [awardsRecognition, setAwardsRecognition] = useState(['']);
  const [screeningQuestions, setScreeningQuestions] = useState({});

  // ✅ Load complete candidate data and field configurations
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // console.log("🔍 Loading candidate data for edit, ID:", initialCandidate.id);
        
        // ✅ FETCH COMPLETE CANDIDATE DATA FROM API
        let fullCandidateData = initialCandidate;
        
        if (initialCandidate.id) {
          try {
            // console.log("📥 Fetching full candidate data from API...");
            const candidateResponse = await candidatesAPI.getApplicationById(initialCandidate.id);
            // console.log("✅ Full candidate data received:", candidateResponse.data);
            fullCandidateData = candidateResponse.data || initialCandidate;
            setCandidateData(fullCandidateData);
          } catch (error) {
            console.warn("⚠️ Could not fetch full candidate data, using initial data:", error);
            fullCandidateData = initialCandidate;
          }
        }

        // ✅ FETCH FIELD CONFIGURATIONS
        // console.log("📥 Fetching field configurations...");
        const response = await candidatesAPI.getFields();
        // console.log("✅ Field configs received:", response.data);

        let configs = [];

        // Parse field configurations
        if (Array.isArray(response.data)) {
          configs = response.data.map((fieldItem, index) => {
            const fieldName = typeof fieldItem === 'string' 
              ? fieldItem 
              : (fieldItem.fieldName || fieldItem.name || "");
            
            const { fieldType, options } = getFieldTypeAndOptions(fieldName);
            
            return {
              fieldName: fieldName,
              displayName: typeof fieldItem === 'object' 
                ? (fieldItem.displayName || formatFieldName(fieldName))
                : formatFieldName(fieldName),
              fieldType: typeof fieldItem === 'object'
                ? (fieldItem.fieldType || fieldType)
                : fieldType,
              isRequired: typeof fieldItem === 'object' ? (fieldItem.isRequired || false) : false,
              orderIndex: typeof fieldItem === 'object' 
                ? (fieldItem.orderIndex ?? index)
                : index,
              options: typeof fieldItem === 'object'
                ? (fieldItem.options || options)
                : options,
              category: typeof fieldItem === 'object'
                ? (fieldItem.category || getCategoryFromFieldName(fieldName))
                : getCategoryFromFieldName(fieldName),
              placeholder: typeof fieldItem === 'object'
                ? (fieldItem.placeholder || getPlaceholderText(fieldName))
                : getPlaceholderText(fieldName),
              helpText: typeof fieldItem === 'object' ? (fieldItem.helpText || "") : "",
              rows: typeof fieldItem === 'object' ? (fieldItem.rows || 4) : 4
            };
          });
        }

        // ✅ Filter out excluded fields (only show fields from getFields API)
        configs = configs.filter(field => field.fieldName && !EXCLUDED_FIELDS.includes(field.fieldName));

        // Sort by orderIndex
        configs.sort((a, b) => a.orderIndex - b.orderIndex);
        
        // console.log(`✅ Loaded ${configs.length} editable field configurations`);
        setFieldConfigs(configs);

        // ✅ Initialize form data with candidate data
        const initialFormData = {};
        configs.forEach(field => {
          let value = fullCandidateData[field.fieldName];
          
          // Format dates for input
          if (field.fieldType === 'date' && value) {
            try {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                value = date.toISOString().split('T')[0];
              }
            } catch (e) {
              console.warn(`Could not parse date for ${field.fieldName}:`, value);
            }
          }
          
          // Convert boolean to string for select fields
          if (BOOLEAN_FIELD_NAMES.includes(field.fieldName)) {
            if (value === true) value = 'Yes';
            else if (value === false) value = 'No';
            else if (value === null) value = 'Maybe';
            else value = '';
          }
          
          initialFormData[field.fieldName] = value !== undefined && value !== null
            ? value 
            : getDefaultValue(field);
        });

        // console.log("✅ Initialized form data with", Object.keys(initialFormData).length, "fields");
        setFormDataState(initialFormData);

        // ✅ Initialize dynamic lists
        setTechnicalSkills(fullCandidateData.technicalSkills?.length > 0 
          ? fullCandidateData.technicalSkills 
          : [{ skillName: '', rating: 3 }]);
        
        setSoftSkills(fullCandidateData.softSkills?.length > 0 
          ? fullCandidateData.softSkills 
          : [{ skillName: '', rating: 3 }]);
        
        setLanguages(fullCandidateData.languages?.length > 0 
          ? fullCandidateData.languages 
          : [{ languageName: '', canRead: false, canWrite: false, canSpeak: false }]);
        
        setPreferredLocations(fullCandidateData.preferredLocations?.length > 0 
          ? fullCandidateData.preferredLocations 
          : ['']);
        
        setKeyAchievements(fullCandidateData.keyAchievements?.length > 0 
          ? fullCandidateData.keyAchievements 
          : ['']);
        
        setAwardsRecognition(fullCandidateData.awardsRecognition?.length > 0 
          ? fullCandidateData.awardsRecognition 
          : ['']);

        // ✅ Initialize screening questions
        setScreeningQuestions(fullCandidateData.screeningQuestionsByJob || {});

      } catch (error) {
        console.error("❌ Error loading data:", error);
        toast.error("Failed to load candidate data");
        setFieldConfigs([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [initialCandidate.id]);

  // Get default value based on field type
  const getDefaultValue = (field) => {
    if (!field) return '';
    
    switch (field.fieldType) {
      case 'checkbox':
        return false;
      case 'number':
        return '';
      case 'select':
        return '';
      case 'date':
        return '';
      case 'multiselect':
        return [];
      default:
        return '';
    }
  };

  // Handle input change
  const handleInputChange = (fieldName, value) => {
    setFormDataState(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear validation error
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Dynamic list handlers
  const handleListItemChange = (listName, index, field, value) => {
    const setters = {
      technicalSkills: setTechnicalSkills,
      softSkills: setSoftSkills,
      languages: setLanguages,
      preferredLocations: setPreferredLocations,
      keyAchievements: setKeyAchievements,
      awardsRecognition: setAwardsRecognition
    };
    
    const setter = setters[listName];
    if (!setter) return;
    
    setter(prev => prev.map((item, i) => 
      i === index 
        ? (typeof item === 'object' ? { ...item, [field]: value } : value)
        : item
    ));
  };

  const addListItem = (listName) => {
    const defaults = {
      technicalSkills: { skillName: '', rating: 3 },
      softSkills: { skillName: '', rating: 3 },
      languages: { languageName: '', canRead: false, canWrite: false, canSpeak: false },
      preferredLocations: '',
      keyAchievements: '',
      awardsRecognition: ''
    };
    
    const setters = {
      technicalSkills: setTechnicalSkills,
      softSkills: setSoftSkills,
      languages: setLanguages,
      preferredLocations: setPreferredLocations,
      keyAchievements: setKeyAchievements,
      awardsRecognition: setAwardsRecognition
    };
    
    const setter = setters[listName];
    const defaultValue = defaults[listName];
    
    if (setter && defaultValue !== undefined) {
      setter(prev => [...prev, defaultValue]);
    }
  };

  const removeListItem = (listName, index) => {
    const setters = {
      technicalSkills: setTechnicalSkills,
      softSkills: setSoftSkills,
      languages: setLanguages,
      preferredLocations: setPreferredLocations,
      keyAchievements: setKeyAchievements,
      awardsRecognition: setAwardsRecognition
    };
    
    const setter = setters[listName];
    if (!setter) return;
    
    setter(prev => {
      if (prev.length > 1) {
        return prev.filter((_, i) => i !== index);
      }
      return prev;
    });
  };

  // ✅ Handle screening question changes
  const handleScreeningQuestionChange = (jobId, questionIndex, field, value) => {
    setScreeningQuestions(prev => {
      const updated = { ...prev };
      if (!updated[jobId]) updated[jobId] = [];
      
      updated[jobId] = updated[jobId].map((qa, idx) => 
        idx === questionIndex ? { ...qa, [field]: value } : qa
      );
      
      return updated;
    });
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    fieldConfigs.forEach(field => {
      if (field.isRequired) {
        const value = formDataState[field.fieldName];
        const displayName = field.displayName || formatFieldName(field.fieldName);
        
        if (value === undefined || value === null || value === '' || 
            (Array.isArray(value) && value.length === 0)) {
          errors[field.fieldName] = `${displayName} is required`;
        }
      }
      
      // Email validation
      if (field.fieldType === 'email' && formDataState[field.fieldName]) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formDataState[field.fieldName])) {
          errors[field.fieldName] = 'Please enter a valid email address';
        }
      }
      
      // Phone validation
      if (field.fieldType === 'tel' && formDataState[field.fieldName]) {
        const phoneDigits = String(formDataState[field.fieldName]).replace(/\D/g, '');
        if (phoneDigits.length < 10) {
          errors[field.fieldName] = 'Please enter a valid 10-digit phone number';
        }
      }
    });
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ✅ Submit form with proper data structure
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fill all required fields correctly');
      return;
    }

    setSubmitting(true);
    try {
      // Build update data object
      const updateData = {};
      
      // ✅ Process ALL fields from formDataState with proper type conversion
      fieldConfigs.forEach(field => {
        const fieldName = field.fieldName;
        let value = formDataState[fieldName];
        
        // Skip if truly missing
        if (value === undefined || value === null) return;
        
        // ✅ FIX: Convert boolean fields properly
        if (BOOLEAN_FIELD_NAMES.includes(fieldName)) {
          if (value === 'Yes' || value === true) {
            updateData[fieldName] = true;
          } else if (value === 'No' || value === false) {
            updateData[fieldName] = false;
          } else if (value === 'Maybe') {
            updateData[fieldName] = null;
          }
          // Do not send empty string for boolean fields
          return;
        }
        
        // ✅ FIX: Convert date fields to LocalDate format
        if (field.fieldType === 'date' && value) {
          if (typeof value === 'string') {
            updateData[fieldName] = value;
          } else if (value instanceof Date) {
            updateData[fieldName] = value.toISOString().split('T')[0];
          }
          return;
        }
        
        // ✅ FIX: Convert number fields to string (backend handles conversion)
        if (field.fieldType === 'number' && value !== '') {
          updateData[fieldName] = String(value);
          return;
        }
        
        // Add regular fields only if not empty string
        if (value !== '') {
          updateData[fieldName] = value;
        }
      });
      
      // ✅ Add dynamic lists (filter out empty entries)
      updateData.technicalSkills = technicalSkills.filter(skill => skill.skillName && skill.skillName.trim());
      updateData.softSkills = softSkills.filter(skill => skill.skillName && skill.skillName.trim());
      updateData.languages = languages.filter(lang => lang.languageName && lang.languageName.trim());
      updateData.preferredLocations = preferredLocations.filter(loc => loc && loc.trim());
      updateData.keyAchievements = keyAchievements.filter(achievement => achievement && achievement.trim());
      updateData.awardsRecognition = awardsRecognition.filter(award => award && award.trim());

      // ✅ Add screening questions
      updateData.screeningQuestionsByJob = screeningQuestions;

      // console.log("📤 Sending update request with data:", updateData);
      // console.log("📊 Total fields being updated:", Object.keys(updateData).length);

      // ✅ BEST FIX: Send data directly as JSON (works with @RequestBody)
      const response = await candidatesAPI.updateApplication(candidateData.id, updateData);
      
      // console.log("✅ Update successful:", response.data);
      toast.success('✅ Candidate updated successfully!');
      
      onSave?.(response.data);
      onClose();
      
    } catch (error) {
      console.error('❌ Error updating candidate:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      const message = error.response?.data?.message || error.message || 'Failed to update candidate. Please try again.';
      toast.error(`Error: ${message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Render field
  const renderField = (field) => {
    if (!field || !field.fieldName) return null;

    const value = formDataState[field.fieldName] ?? '';
    const error = validationErrors[field.fieldName];
    
    const displayName = field.displayName || formatFieldName(field.fieldName);
    const placeholderText = field.placeholder || `Enter ${displayName.toLowerCase()}...`;
    
    const baseClasses = "w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200";
    const errorClasses = "border-red-500 bg-red-50";
    const normalClasses = "border-gray-300 bg-white hover:border-blue-400";
    
    switch (field.fieldType) {
      case 'text':
      case 'email':
      case 'tel':
      case 'url':
        return (
          <div className="space-y-1">
            <input
              type={field.fieldType}
              value={value}
              onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
              className={`${baseClasses} ${error ? errorClasses : normalClasses}`}
              placeholder={placeholderText}
              disabled={loading || submitting}
              autoComplete="off"
            />
            {error && (
              <div className="flex items-center gap-1 text-red-600 text-sm">
                <AlertCircle size={14} />
                <span>{error}</span>
              </div>
            )}
          </div>
        );
      
      case 'number':
        return (
          <div className="space-y-1">
            <input
              type="number"
              value={value}
              onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
              className={`${baseClasses} ${error ? errorClasses : normalClasses}`}
              placeholder={placeholderText}
              disabled={loading || submitting}
            />
            {error && (
              <div className="flex items-center gap-1 text-red-600 text-sm">
                <AlertCircle size={14} />
                <span>{error}</span>
              </div>
            )}
          </div>
        );
      
      case 'textarea':
        return (
          <div className="space-y-1">
            <textarea
              value={value}
              onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
              className={`${baseClasses} resize-none min-h-[100px] ${error ? errorClasses : normalClasses}`}
              placeholder={placeholderText}
              disabled={loading || submitting}
              rows={field.rows || 4}
            />
            {error && (
              <div className="flex items-center gap-1 text-red-600 text-sm">
                <AlertCircle size={14} />
                <span>{error}</span>
              </div>
            )}
          </div>
        );
      
      case 'select':
        return (
          <div className="space-y-1">
            <select
              value={value}
              onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
              className={`${baseClasses} cursor-pointer ${error ? errorClasses : normalClasses}`}
              disabled={loading || submitting}
            >
              <option value="">Select {displayName}...</option>
              {field.options?.map((option, index) => (
                <option key={index} value={option} className="py-2">{option}</option>
              ))}
            </select>
            {error && (
              <div className="flex items-center gap-1 text-red-600 text-sm">
                <AlertCircle size={14} />
                <span>{error}</span>
              </div>
            )}
          </div>
        );
      
      case 'date':
        return (
          <div className="space-y-1">
            <div className="relative">
              <input
                type="date"
                value={value}
                onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
                className={`${baseClasses} pr-10 ${error ? errorClasses : normalClasses}`}
                disabled={loading || submitting}
              />
              <Calendar className="absolute right-3 top-3.5 text-gray-400 pointer-events-none" size={20} />
            </div>
            {error && (
              <div className="flex items-center gap-1 text-red-600 text-sm">
                <AlertCircle size={14} />
                <span>{error}</span>
              </div>
            )}
          </div>
        );
      
      case 'checkbox':
        return (
          <div className="space-y-1">
            <label className="flex items-center space-x-3 cursor-pointer group">
              <div className={`relative w-5 h-5 rounded border flex items-center justify-center transition-all duration-200 ${
                error ? 'border-red-500' : 'border-gray-300 group-hover:border-blue-400'
              }`}>
                <input
                  type="checkbox"
                  checked={!!value}
                  onChange={(e) => handleInputChange(field.fieldName, e.target.checked)}
                  className="absolute opacity-0 w-full h-full cursor-pointer"
                  disabled={loading || submitting}
                />
                {value && <Check size={14} className="text-blue-600" />}
              </div>
              <span className="text-gray-700 select-none text-sm">{displayName}</span>
            </label>
            {error && (
              <div className="flex items-center gap-1 text-red-600 text-sm">
                <AlertCircle size={14} />
                <span>{error}</span>
              </div>
            )}
          </div>
        );
      
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
            className={`${baseClasses} ${error ? errorClasses : normalClasses}`}
            placeholder={placeholderText}
            disabled={loading || submitting}
            autoComplete="off"
          />
        );
    }
  };

  // Group fields by category
  const getFieldsByCategory = (category) => {
    return fieldConfigs.filter(field => {
      const fieldCategory = field.category || 'Other';
      return fieldCategory === category;
    });
  };

  // Get unique categories
  const getCategories = () => {
    const allCategories = fieldConfigs.map(field => field.category || 'Other');
    const uniqueCategories = [...new Set(allCategories)];
    
    const categoryOrder = [
      'Personal', 'Contact', 'Address', 'Employment', 'Compensation', 
      'Education', 'Work Authorization', 'Skills', 'Languages', 
      'Location', 'References', 'Consents', 'Documents', 'Other'
    ];
    
    return uniqueCategories.sort((a, b) => {
      const indexA = categoryOrder.indexOf(a);
      const indexB = categoryOrder.indexOf(b);
      
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
  };

  // ✅ Check if any skills/achievements data exists
  const hasSkillsData = () => {
    return (
      (technicalSkills.length > 0 && technicalSkills[0].skillName !== '') ||
      (softSkills.length > 0 && softSkills[0].skillName !== '') ||
      (keyAchievements.length > 0 && keyAchievements[0] !== '') ||
      (awardsRecognition.length > 0 && awardsRecognition[0] !== '')
    );
  };

  // ✅ Check if languages data exists
  const hasLanguagesData = () => {
    return languages.length > 0 && languages[0].languageName !== '';
  };

  // ✅ Check if preferred locations data exists
  const hasPreferredLocationsData = () => {
    return preferredLocations.length > 0 && preferredLocations[0] !== '';
  };

  // ✅ Check if screening questions exist
  const hasScreeningQuestions = () => {
    return screeningQuestions && Object.keys(screeningQuestions).length > 0;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm">
        <div className="bg-white shadow-2xl w-full max-w-4xl h-full flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold text-gray-900">Loading Candidate Data...</h3>
            <p className="text-sm text-gray-500 mt-2">Fetching complete application details</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm">
      <div className="bg-white shadow-2xl w-full max-w-4xl h-full flex flex-col animate-slideInRight overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Edit Candidate</h2>
            <p className="text-sm text-gray-600 mt-1">
              Application ID: {candidateData.applicationId || 'N/A'}
            </p>
            {candidateData.primaryEmail && (
              <p className="text-sm text-blue-600 mt-1">
                📧 {candidateData.primaryEmail}
              </p>
            )}
          </div>
          <button 
            onClick={onClose} 
            disabled={submitting}
            className="p-2 hover:bg-white rounded-full transition-colors disabled:opacity-50"
          >
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {fieldConfigs.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Editable Fields</h3>
              <p className="text-gray-600">No field configurations found for editing.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* ✅ Display Resume URL if available */}
              {candidateData.resumeUrl && (
                <section className="space-y-4 bg-blue-50 p-6 rounded-lg border-2 border-blue-200">
                  <div className="flex items-center gap-2">
                    <FileText size={20} className="text-blue-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Resume</h3>
                  </div>
                  <a
                    href={candidateData.resumeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <ExternalLink size={16} />
                    <span>Click to view resume in new tab</span>
                  </a>
                </section>
              )}

              {/* ✅ Editable Screening Questions */}
              {hasScreeningQuestions() && (
                <section className="space-y-4 bg-indigo-50 p-6 rounded-lg border-2 border-indigo-200">
                  <div className="flex items-center gap-2">
                    <AlertCircle size={20} className="text-indigo-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Screening Questions</h3>
                    <span className="text-sm text-indigo-600">(Editable)</span>
                  </div>
                  
                  {Object.entries(screeningQuestions).map(([jobId, questions]) => (
                    <div key={jobId} className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-indigo-700 font-medium">
                        <Briefcase size={16} />
                        <span>Job: {jobId}</span>
                      </div>
                      
                      <div className="space-y-3">
                        {Array.isArray(questions) && questions.map((qa, index) => (
                          <div key={index} className="bg-white p-4 rounded-lg border border-indigo-200 shadow-sm">
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                                {index + 1}
                              </div>
                              <div className="flex-1 space-y-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Question
                                  </label>
                                  <input
                                    type="text"
                                    value={qa.question || ''}
                                    onChange={(e) => handleScreeningQuestionChange(jobId, index, 'question', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    placeholder="Enter question..."
                                    disabled={submitting}
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Answer
                                  </label>
                                  <textarea
                                    value={qa.answer || ''}
                                    onChange={(e) => handleScreeningQuestionChange(jobId, index, 'answer', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                                    placeholder="Enter answer..."
                                    rows={3}
                                    disabled={submitting}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </section>
              )}

              {/* Render fields by category */}
              {getCategories().map(category => {
                const categoryFields = getFieldsByCategory(category);
                if (categoryFields.length === 0) return null;
                
                return (
                  <section key={category} className="space-y-4">
                    <div className="flex items-center gap-2">
                      {getCategoryIcon(category)}
                      <h3 className="text-lg font-semibold text-gray-900">{category}</h3>
                      <span className="text-sm text-gray-500">({categoryFields.length} fields)</span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {categoryFields.map(field => (
                        <div 
                          key={field.fieldName} 
                          className={field.fieldType === 'textarea' ? 'md:col-span-2' : ''}
                        >
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {field.displayName || formatFieldName(field.fieldName)}
                            {field.isRequired && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          {renderField(field)}
                          {field.helpText && (
                            <p className="text-xs text-gray-500 mt-1">{field.helpText}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })}

              {/* ✅ Skills & Achievements Section - Conditional rendering */}
              {hasSkillsData() && (
                <section className="space-y-6">
                  <div className="flex items-center gap-2">
                    <Award size={20} className="text-yellow-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Skills & Achievements</h3>
                  </div>
                  
                  <div className="space-y-6">
                    {/* Technical Skills */}
                    {technicalSkills.length > 0 && technicalSkills[0].skillName !== '' && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900">Technical Skills</h4>
                          <button
                            type="button"
                            onClick={() => addListItem('technicalSkills')}
                            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
                            disabled={submitting}
                          >
                            <Plus size={16} />
                            Add Skill
                          </button>
                        </div>
                        {technicalSkills.map((skill, index) => (
                          <div key={index} className="flex items-center gap-3">
                            <input
                              type="text"
                              value={skill.skillName}
                              onChange={(e) => handleListItemChange('technicalSkills', index, 'skillName', e.target.value)}
                              className="flex-1 px-4 py-3 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="e.g., React, Python, AWS..."
                              disabled={submitting}
                              autoComplete="off"
                            />
                            <select
                              value={skill.rating}
                              onChange={(e) => handleListItemChange('technicalSkills', index, 'rating', parseInt(e.target.value))}
                              className="w-40 px-4 py-3 rounded-lg border border-gray-300 bg-white focus:ring-2 focus:ring-blue-500 cursor-pointer"
                              disabled={submitting}
                            >
                              <option value="1">⭐ Beginner</option>
                              <option value="2">⭐⭐ Basic</option>
                              <option value="3">⭐⭐⭐ Intermediate</option>
                              <option value="4">⭐⭐⭐⭐ Advanced</option>
                              <option value="5">⭐⭐⭐⭐⭐ Expert</option>
                            </select>
                            {technicalSkills.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeListItem('technicalSkills', index)}
                                className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                                disabled={submitting}
                              >
                                <Trash2 size={18} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Soft Skills */}
                    {softSkills.length > 0 && softSkills[0].skillName !== '' && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900">Soft Skills</h4>
                          <button
                            type="button"
                            onClick={() => addListItem('softSkills')}
                            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
                            disabled={submitting}
                          >
                            <Plus size={16} />
                            Add Skill
                          </button>
                        </div>
                        {softSkills.map((skill, index) => (
                          <div key={index} className="flex items-center gap-3">
                            <input
                              type="text"
                              value={skill.skillName}
                              onChange={(e) => handleListItemChange('softSkills', index, 'skillName', e.target.value)}
                              className="flex-1 px-4 py-3 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="e.g., Leadership, Communication..."
                              disabled={submitting}
                              autoComplete="off"
                            />
                            <select
                              value={skill.rating}
                              onChange={(e) => handleListItemChange('softSkills', index, 'rating', parseInt(e.target.value))}
                              className="w-40 px-4 py-3 rounded-lg border border-gray-300 bg-white focus:ring-2 focus:ring-blue-500 cursor-pointer"
                              disabled={submitting}
                            >
                              <option value="1">⭐ Beginner</option>
                              <option value="2">⭐⭐ Basic</option>
                              <option value="3">⭐⭐⭐ Intermediate</option>
                              <option value="4">⭐⭐⭐⭐ Advanced</option>
                              <option value="5">⭐⭐⭐⭐⭐ Expert</option>
                            </select>
                            {softSkills.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeListItem('softSkills', index)}
                                className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                                disabled={submitting}
                              >
                                <Trash2 size={18} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Key Achievements */}
                    {keyAchievements.length > 0 && keyAchievements[0] !== '' && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900">Key Achievements</h4>
                          <button
                            type="button"
                            onClick={() => addListItem('keyAchievements')}
                            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
                            disabled={submitting}
                          >
                            <Plus size={16} />
                            Add Achievement
                          </button>
                        </div>
                        {keyAchievements.map((achievement, index) => (
                          <div key={index} className="flex items-start gap-3">
                            <textarea
                              value={achievement || ''}
                              onChange={(e) => handleListItemChange('keyAchievements', index, null, e.target.value)}
                              className="flex-1 px-4 py-3 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                              placeholder="Describe your achievement..."
                              disabled={submitting}
                              rows={2}
                            />
                            {keyAchievements.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeListItem('keyAchievements', index)}
                                className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg flex-shrink-0"
                                disabled={submitting}
                              >
                                <Trash2 size={18} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Awards & Recognition */}
                    {awardsRecognition.length > 0 && awardsRecognition[0] !== '' && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900">Awards & Recognition</h4>
                          <button
                            type="button"
                            onClick={() => addListItem('awardsRecognition')}
                            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
                            disabled={submitting}
                          >
                            <Plus size={16} />
                            Add Award
                          </button>
                        </div>
                        {awardsRecognition.map((award, index) => (
                          <div key={index} className="flex items-center gap-3">
                            <input
                              type="text"
                              value={award}
                              onChange={(e) => handleListItemChange('awardsRecognition', index, null, e.target.value)}
                              className="flex-1 px-4 py-3 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="e.g., Employee of the Year 2023..."
                              disabled={submitting}
                              autoComplete="off"
                            />
                            {awardsRecognition.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeListItem('awardsRecognition', index)}
                                className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                                disabled={submitting}
                              >
                                <Trash2 size={18} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* ✅ Languages Section - Conditional rendering */}
              {hasLanguagesData() && (
                <section className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Globe size={20} className="text-indigo-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Languages</h3>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900">Language Proficiency</h4>
                      <button
                        type="button"
                        onClick={() => addListItem('languages')}
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
                        disabled={submitting}
                      >
                        <Plus size={16} />
                        Add Language
                      </button>
                    </div>
                    {languages.map((language, index) => (
                      <div key={index} className="p-4 border border-gray-200 rounded-lg space-y-3 bg-gray-50">
                        <div className="flex items-center gap-3">
                          <input
                            type="text"
                            value={language.languageName}
                            onChange={(e) => handleListItemChange('languages', index, 'languageName', e.target.value)}
                            className="flex-1 px-4 py-3 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., English, Hindi, Spanish..."
                            disabled={submitting}
                            autoComplete="off"
                          />
                          {languages.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeListItem('languages', index)}
                              className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                              disabled={submitting}
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-4">
                          {['canRead', 'canWrite', 'canSpeak'].map(cap => (
                            <label key={cap} className="flex items-center cursor-pointer hover:bg-white px-3 py-2 rounded-lg transition-colors">
                              <input
                                type="checkbox"
                                checked={language[cap]}
                                onChange={(e) => handleListItemChange('languages', index, cap, e.target.checked)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2 w-4 h-4"
                                disabled={submitting}
                              />
                              <span className="text-sm text-gray-700 font-medium">{cap.replace('can', '')}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* ✅ Preferred Locations Section - Conditional rendering */}
              {hasPreferredLocationsData() && (
                <section className="space-y-4">
                  <div className="flex items-center gap-2">
                    <MapPin size={20} className="text-orange-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Preferred Locations</h3>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900">Location Preferences</h4>
                      <button
                        type="button"
                        onClick={() => addListItem('preferredLocations')}
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
                        disabled={submitting}
                      >
                        <Plus size={16} />
                        Add Location
                      </button>
                    </div>
                    {preferredLocations.map((location, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <input
                          type="text"
                          value={location}
                          onChange={(e) => handleListItemChange('preferredLocations', index, null, e.target.value)}
                          className="flex-1 px-4 py-3 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Hyderabad, Bangalore, Remote..."
                          disabled={submitting}
                          autoComplete="off"
                        />
                        {preferredLocations.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeListItem('preferredLocations', index)}
                            className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                            disabled={submitting}
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button 
            onClick={onClose}
            disabled={submitting}
            className="px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button 
            onClick={handleSubmit}
            disabled={submitting || fieldConfigs.length === 0}
            className="px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white"></div>
                Updating...
              </>
            ) : (
              <>
                <Save size={20} />
                Update Candidate
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditCandidateModal;